import { z } from "zod";
import { RecurringExceptionsSchema } from "./recurring-exception.js";

// ── Calendário de operação por linha (turnos semanais + exceções datadas) ─────────────────────
// Convenção de meia-noite: endMinute > 1440 significa "atravessa para o dia seguinte"
// (22h–06h = 1320→1800). Horas são LOCAIS da fábrica (timezone IANA da organização); a conversão
// para UTC vive exclusivamente no helper lib/operating-calendar.ts.

export const WeeklyShiftSchema = z
  .object({
    weekday: z.number().int().min(0).max(6), // 0 = domingo (alinha com Date#getDay)
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(2879),
    label: z.string().trim().max(60).optional(),
  })
  .refine((s) => s.endMinute > s.startMinute, {
    message: "O fim do turno deve ser depois do início",
    path: ["endMinute"],
  });
export type WeeklyShift = z.infer<typeof WeeklyShiftSchema>;

export const CalendarExceptionSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("CLOSED"), // derruba TODAS as janelas do dia (feriado/manutenção)
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data deve estar no formato AAAA-MM-DD"),
      reason: z.string().trim().max(200).optional(),
    }),
    z.object({
      kind: z.literal("EXTRA_WINDOW"), // adiciona janela operacional (hora extra)
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data deve estar no formato AAAA-MM-DD"),
      startMinute: z.number().int().min(0).max(1439),
      endMinute: z.number().int().min(1).max(2879),
      reason: z.string().trim().max(200).optional(),
    }),
  ])
  .refine((e) => e.kind !== "EXTRA_WINDOW" || e.endMinute > e.startMinute, {
    message: "O fim da janela extra deve ser depois do início",
    path: ["endMinute"],
  })
  .refine((e) => !Number.isNaN(Date.parse(`${e.date}T00:00:00Z`)), {
    message: "Data inválida",
    path: ["date"],
  });
export type CalendarException = z.infer<typeof CalendarExceptionSchema>;

const WEEK_MINUTES = 7 * 1440;

// Materializa um turno em segmentos [início, fim) na régua da semana (minutos 0..10080),
// com wrap do sábado→domingo — a sobreposição é checada considerando o overflow da meia-noite.
function toWeekSegments(
  shift: Pick<WeeklyShift, "weekday" | "startMinute" | "endMinute">
): [number, number][] {
  const start = shift.weekday * 1440 + shift.startMinute;
  const end = shift.weekday * 1440 + shift.endMinute;
  if (end <= WEEK_MINUTES) return [[start, end]];
  return [
    [start, WEEK_MINUTES],
    [0, end - WEEK_MINUTES],
  ];
}

/**
 * Exportada porque o calendário da ORGANIZAÇÃO valida a mesma invariante. Duplicar a
 * checagem de sobreposição num segundo arquivo é como as duas resoluções de expediente começariam
 * a divergir: uma aceita o que a outra recusa, e o operador descobre pelo indicador.
 */
export function hasWeeklyOverlap(shifts: WeeklyShift[]): boolean {
  const segments = shifts.flatMap((s) => toWeekSegments(s));
  segments.sort((a, b) => a[0] - b[0]);
  for (let i = 1; i < segments.length; i++) {
    const prev = segments[i - 1];
    const curr = segments[i];
    if (prev && curr && curr[0] < prev[1]) return true;
  }
  return false;
}

// ── Tetos (research) ────────────────────────────────────────────────────────────
// Existem para conter payload absurdo, não para modelar uso real.
export const MAX_WEEKLY_SHIFTS = 50;
/**
 * 366 → 4000. Feriados nacionais são ~13/ano e não são podados: o expediente vigente é aplicado a
 * QUALQUER período consultado, então apagar uma data de 2026 reescreveria o OEE de 2026. Sem poda,
 * o teto precisa comportar uma década de uso intenso (~400/ano incluindo paradas datadas).
 */
export const MAX_DATED_EXCEPTIONS = 4000;

export const operatingCalendarFields = {
  weeklyShifts: z.array(WeeklyShiftSchema).max(MAX_WEEKLY_SHIFTS),
  exceptions: z.array(CalendarExceptionSchema).max(MAX_DATED_EXCEPTIONS),
};

/**
 * As duas invariantes que valem para QUALQUER calendário — o da linha e o da organização. Ficam
 * numa função só porque são a mesma regra: reescrevê-las no contrato da organização seria criar
 * dois lugares onde "não pode haver duas exceções na mesma data" pode deixar de ser verdade, e
 * essa premissa é exatamente a que o `LEFT JOIN` de `day_ex` no SQL consome.
 */
export function checkCalendarInvariants(
  c: { weeklyShifts: WeeklyShift[]; exceptions: CalendarException[] },
  ctx: z.RefinementCtx
): void {
  if (hasWeeklyOverlap(c.weeklyShifts)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Turnos não podem se sobrepor (considere turnos que cruzam a meia-noite)",
      path: ["weeklyShifts"],
    });
  }
  if (new Set(c.exceptions.map((e) => e.date)).size !== c.exceptions.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Não pode haver mais de uma exceção para a mesma data",
      path: ["exceptions"],
    });
  }
}

/**
 * O calendário na forma que os expansores consomem: turnos semanais + exceções DATADAS.
 *
 * Esta forma NÃO ganhou campos, e isso é deliberado. Camadas e recorrência são resolvidas
 * ANTES, por `mergeCalendarLayers`/`expandRecurrence`, que devolvem exatamente este objeto. Assim o
 * expansor com a lógica de DST (lib/operating-calendar.ts, quatro casos de paridade provados)
 * permanece intocado, e `OperatingCalendar` passa a significar "expediente EFETIVO" sem que nenhum
 * consumidor existente precise mudar.
 */
export const OperatingCalendarSchema = z
  .object(operatingCalendarFields)
  .superRefine(checkCalendarInvariants);
export type OperatingCalendar = z.infer<typeof OperatingCalendarSchema>;

// ── O calendário DA LINHA (o que trafega no PUT/GET da linha) ──────────────────────────────────
// Diferente de `OperatingCalendarSchema`, que é o expediente EFETIVO já resolvido. A linha declara
// mais do que consome: ela também diz se herda os turnos da fábrica.

export const SHIFTS_MODES = ["INHERIT", "OVERRIDE"] as const;
export const ShiftsModeSchema = z.enum(SHIFTS_MODES);
export type ShiftsMode = z.infer<typeof ShiftsModeSchema>;

export const LineOperatingCalendarSchema = z
  .object({
    ...operatingCalendarFields,
    recurringExceptions: RecurringExceptionsSchema.default([]),
    /**
     * Default `OVERRIDE` PRESERVA o comportamento atual: hoje, os turnos que a linha declara são
     * os turnos que ela usa. Um default `INHERIT` faria toda linha que já declarou expediente
     * passar a herdar — o indicador dela mudaria sozinho. A migração faz o backfill
     * equivalente para as linhas que já existem; este default cobre o payload que chega sem o
     * campo (cliente antigo).
     *
     * `OVERRIDE` com `weeklyShifts` vazio continua significando LINHA FECHADA, não "segue o
     * padrão" — é o estado do meio do contrato de três estados.
     */
    shiftsMode: ShiftsModeSchema.default("OVERRIDE"),
  })
  .superRefine(checkCalendarInvariants);
export type LineOperatingCalendar = z.infer<typeof LineOperatingCalendarSchema>;

export const UpsertLineCalendarRequestSchema = LineOperatingCalendarSchema;
export type UpsertLineCalendarRequest = z.infer<typeof UpsertLineCalendarRequestSchema>;
/**
 * O que o CLIENTE envia — `recurringExceptions` e `shiftsMode` são opcionais aqui porque têm
 * `.default()`. É este o tipo que a mutation do frontend deve aceitar: usar o `z.infer` (a saída,
 * já com os defaults aplicados) obrigaria toda tela a repetir dois campos que o schema existe
 * justamente para preencher.
 */
export type UpsertLineCalendarRequestInput = z.input<typeof UpsertLineCalendarRequestSchema>;

export const LineCalendarEnvelopeSchema = z.object({
  data: LineOperatingCalendarSchema.nullable(),
  error: z.null(),
});
export type LineCalendarEnvelope = z.infer<typeof LineCalendarEnvelopeSchema>;

// ── Cenário: escolha de calendário (research — tri-state explícito) ────────────────────────
// null/ausente no Scenario.calendarOverride ⇒ default: inherit (com linha base) / always_on
// (linha nova). A cópia `custom` é dado do cenário — NUNCA retro-propaga para a linha real.
export const ScenarioCalendarOverrideSchema = z
  .object({
    mode: z.enum(["inherit", "always_on", "custom"]),
    custom: OperatingCalendarSchema.optional(),
  })
  .refine((o) => (o.mode === "custom") === (o.custom !== undefined), {
    message: "Modo 'custom' exige a cópia do calendário (e só ele pode tê-la)",
    path: ["custom"],
  });
export type ScenarioCalendarOverride = z.infer<typeof ScenarioCalendarOverrideSchema>;
